import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, CircularProgress } from "@mui/material";
import { Lock, TaskAlt, Shortcut, PeopleAlt } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks";
import { getAnalytics } from "../../../../redux/actions";
import { fetchError } from "../../../../redux/actions";
import { BarChart } from "./components/BarChart";
import { AreaChart } from "./components/AreaChart";
import { MetricCard } from "./components/MetricCard";
import { DoughnutChart } from "./components/DoughnutChart";
import type { ChartData } from "chart.js";

interface AnalyticData {
    date: string,
    view_ar: number,
    unique_view_ar: number,
    user_agent: UserAgentData[]
}

interface UserAgentData {
    device: string,
    count: number
}

interface SessionData {
    date: string,
    view_session: number,
    unique_view_session: number,
}

type TimeRange = "lastWeek" | "lastMonth";

const pieColors = [
    "#8295AE", "#8ABAED", "#A1D4F8", "#FF671F", "#F9DEC9", "#0C2340", "#48586E", "#4E93DB", "#7BAFD4", "#F38C5C", "#F6DCD5", "#2B66B1"
];

function formatDeltaPercentage(curr: number, prev: number): string {
    const delta = curr - prev;
    if (prev === 0 && curr === 0) return "+0%";
    if (prev === 0 && curr > 0) return "+∞%";
    if (prev > 0 && curr === 0) return "-100%";
    const percent = (delta / prev) * 100;
    return `${delta > 0 ? "+" : ""}${Math.round(percent)}%`;
}

const Analytics = () => {
    const dispatch = useAppDispatch();
    const businessGroupState = useAppSelector(state => state.businessGroup);
    const [timeRange, setTimeRange] = useState<TimeRange>("lastWeek");
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState<{
        viewCounts: AnalyticData[],
        viewSessions: SessionData[],
        thisWeekUniqueUsers: number,
        lastWeekUniqueUsers: number,
        thisWeekBeaconViews: { id: string, name: string, views: number }[],
        thisWeekBeaconLinks: { id: string, name: string, views: number }[],
        lastWeekBeaconViews: { id: string; name: string; views: number }[],
        lastWeekBeaconLinks: { id: string; name: string; views: number }[],
    } | null>(null);

    useEffect(() => {
        const group = businessGroupState.currentBusinessGroup;
        if (!group) return;

        setLoading(true);
        setAnalytics(null);
        dispatch(getAnalytics(group.id, timeRange, (data) => {
            if (data?.viewCounts) {
                setAnalytics(data);
            } else {
                dispatch(fetchError("Failed to receive analytic data"));
            }
            setLoading(false);
        }));
    }, [businessGroupState.currentBusinessGroup, timeRange]);

    const labelMap = {
        lastWeek: {
            periodTitle: "Last 7 Days",
            currentLabel: "This Week",
            previousLabel: "Last Week"
        },
        lastMonth: {
            periodTitle: "Last 30 Days",
            currentLabel: "This Month",
            previousLabel: "Last Month"
        }
    }[timeRange];

    function parseUsDateToUtc(dateStr: string): Date {
        const [month, day, year] = dateStr.split("-").map(Number);
        return new Date(Date.UTC(year, month - 1, day));
    }

    const sortedViewCounts = useMemo(() => {
        if (!analytics?.viewCounts) return [];
        const counts = [...analytics.viewCounts];
        counts.sort(
            (a, b) => parseUsDateToUtc(a.date).getTime() - parseUsDateToUtc(b.date).getTime()
        );
        return counts;
    }, [analytics]);

    const sortedViewSessions = useMemo(() => {
        if (!analytics?.viewSessions) return [];
        const counts = [...analytics.viewSessions];
        counts.sort(
            (a, b) => parseUsDateToUtc(a.date).getTime() - parseUsDateToUtc(b.date).getTime()
        );
        return counts;
    }, [analytics]);

    const viewCountsChartData = useMemo<ChartData<"line", number[], string>>(() => {
        return {
            labels: sortedViewCounts.map(d => d.date),
            datasets: [
                {
                    label: "Total Launches",
                    data: sortedViewCounts.map(d => d.view_ar),
                    borderColor: "#1d2c4f",
                    backgroundColor: "#1d2c4f",
                    fill: true,
                    tension: 0.3
                },
                {
                    label: "Unique Users",
                    data: sortedViewCounts.map(d => d.unique_view_ar),
                    borderColor: "#a0c7eb",
                    backgroundColor: "#a0c7eb",
                    fill: true,
                    tension: 0.3
                }
            ]
        };
    }, [sortedViewCounts]);

    const experienceViewStats = useMemo(() => {
        if (sortedViewCounts.length === 0) return null;

        const len = sortedViewCounts.length;
        const periodDays = timeRange === "lastMonth" ? 30 : 7;

        const currentPeriodData = sortedViewCounts.slice(Math.max(0, len - periodDays), len);
        const previousPeriodData = sortedViewCounts.slice(Math.max(0, len - 2 * periodDays), Math.max(0, len - periodDays));

        const sum = (arr: AnalyticData[]) => arr.reduce((t, d) => t + d.view_ar, 0);
        const curr = sum(currentPeriodData);
        const prev = sum(previousPeriodData);
        const delta = curr - prev;
        return {
            value: curr.toLocaleString(),
            change: delta,
            percentage: formatDeltaPercentage(curr, prev)
        };
    }, [sortedViewCounts, timeRange]);

    const viewSessionStats = useMemo(() => {
        if (sortedViewSessions.length === 0) return null;

        const len = sortedViewSessions.length;
        const periodDays = timeRange === "lastMonth" ? 30 : 7;

        const currentPeriodData = sortedViewSessions.slice(Math.max(0, len - periodDays), len);
        const previousPeriodData = sortedViewSessions.slice(Math.max(0, len - 2 * periodDays),
            Math.max(0, len - periodDays));

        const sum = (arr: SessionData[]) => arr.reduce((t, d) => t + d.view_session, 0);

        const curr = sum(currentPeriodData);
        const prev = sum(previousPeriodData);
        const delta = curr - prev;

        return {
            value: curr.toLocaleString(),
            change: delta,
            percentage: formatDeltaPercentage(curr, prev)
        };
    }, [sortedViewSessions, timeRange]);

    const uniqueUsersStats = useMemo(() => {
        if (!analytics) return null;

        const curr = analytics.thisWeekUniqueUsers;
        const prev = analytics.lastWeekUniqueUsers;
        const delta = curr - prev;

        return {
            value: curr.toLocaleString(),
            change: delta,
            percentage: formatDeltaPercentage(curr, prev)
        };
    }, [analytics]);

    const beaconTapsStats = useMemo(() => {
        if (!analytics?.thisWeekBeaconViews) return null;

        const curr = analytics.thisWeekBeaconViews.reduce((sum, b) => sum + (b.views || 0), 0);
        const prev = analytics.lastWeekBeaconViews.reduce((sum, b) => sum + (b.views || 0), 0);
        const delta = curr - prev;

        return {
            value: curr.toLocaleString(),
            change: delta,
            percentage: formatDeltaPercentage(curr, prev)
        };
    }, [analytics]);

    const linkClicksStats = useMemo(() => {
        if (!analytics?.thisWeekBeaconLinks) return null;

        const curr = analytics.thisWeekBeaconLinks.reduce((sum, b) => sum + (b.views || 0), 0);
        const prev = analytics.lastWeekBeaconLinks.reduce((sum, b) => sum + (b.views || 0), 0);
        const delta = curr - prev;

        return {
            value: curr.toLocaleString(),
            change: delta,
            percentage: formatDeltaPercentage(curr, prev)
        };
    }, [analytics]);

    const beaconInteractionsChartData = useMemo<ChartData<"bar", number[], string>>(() => {
        if (!analytics) return { labels: [], datasets: [] };

        const tapsByBeacon = analytics.thisWeekBeaconViews || [];
        const clicksByBeacon = analytics.thisWeekBeaconLinks || [];
        const beaconMap = new Map<string, { taps: number; clicks: number }>();

        for (const b of tapsByBeacon) {
            beaconMap.set(b.name, { taps: b.views, clicks: 0 });
        }

        for (const b of clicksByBeacon) {
            if (beaconMap.has(b.name)) {
                beaconMap.get(b.name)!.clicks = b.views;
            } else {
                beaconMap.set(b.name, { taps: 0, clicks: b.views });
            }
        }

        const sortedEntries = Array.from(beaconMap.entries()).sort(
            (a, b) => b[1].taps - a[1].taps
        );

        const labels = sortedEntries.map(([name]) => name);
        const taps = sortedEntries.map(([_, data]) => data.taps);
        const clicks = sortedEntries.map(([_, data]) => data.clicks);

        return {
            labels,
            datasets: [
                {
                    label: "Beacon Taps",
                    data: taps,
                    backgroundColor: "#e98045"
                },
                {
                    label: "Links Clicked",
                    data: clicks,
                    backgroundColor: "#6b7990"
                }
            ]
        };
    }, [analytics]);

    const viewSessionsChartData = useMemo<ChartData<"line", number[], string>>(() => {
        return {
            labels: sortedViewSessions.map(d => d.date),
            datasets: [
                {
                    label: "Total Sessions",
                    data: sortedViewSessions.map(d => d.view_session),
                    borderColor: "#e38a3f",
                    backgroundColor: "#e38a3f",
                    fill: true,
                    tension: 0.3
                },
                {
                    label: "Unique Sessions",
                    data: sortedViewSessions.map(d => d.unique_view_session),
                    borderColor: "#ffcba4",
                    backgroundColor: "#ffcba4",
                    fill: true,
                    tension: 0.3
                }
            ]
        };
    }, [sortedViewSessions]);

    const totalAgentViews = useMemo(() => {
        if (!sortedViewCounts || sortedViewCounts.length === 0) return "";

        const len = sortedViewCounts.length;
        const periodDays = timeRange === "lastMonth" ? 30 : 7;

        const currentPeriodData = sortedViewCounts.slice(Math.max(0, len - periodDays), len);
        
        const total = currentPeriodData.reduce((sum, day) =>
            sum + day.user_agent.reduce((s, u) => s + u.count, 0), 0);

        return total.toLocaleString();
    }, [sortedViewCounts, timeRange]);


    const userAgentChartData = useMemo<ChartData<"doughnut", number[], string>>(() => {
       if (!sortedViewCounts || sortedViewCounts.length === 0) {
            return { labels: [], datasets: [] };
        }

        const fixedColorMap: Record<string, string> = {
            "iOS Web Browser": "#f4dfcb",
            "iOS App Clip": "#5f91d8",
            "iOS App": "#12233f",
            "Android App": "#ed7231"
        };

        const labels: string[] = [];
        const data: number[] = [];
        const backgroundColors: string[] = [];

        let pieColorIndex = 0;

        const deviceCounts = new Map<string, number>();

        for (const dailyAnalytic of sortedViewCounts) {
            for (const user_agent of dailyAnalytic.user_agent) {
                const label = user_agent.device ?? "Unknown";
                deviceCounts.set(label, (deviceCounts.get(label) ?? 0) + user_agent.count);
            }
        }

        Array.from(deviceCounts.entries()).forEach(([label, count]) => {
            labels.push(label);
            data.push(count);

            if (fixedColorMap[label]) {
                backgroundColors.push(fixedColorMap[label]);
            } else {
                backgroundColors.push(pieColors[pieColorIndex % pieColors.length]);
                pieColorIndex++;
            }
        });

        return {
            labels,
            datasets: [{
                data,
                backgroundColor: backgroundColors
            }]
        };
    }, [sortedViewCounts]);

    if (loading) {
        return (
            <Box
                sx={{
                    height: "400px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    } else {
        return (
            <>
                <FormControl
                    variant="outlined"
                    size="small"
                    sx={{
                        minWidth: 180,
                        mb: 3,
                        backgroundColor: "#f5f7fa",
                        borderRadius: 2,
                        boxShadow: 1,
                    }}
                >
                    <InputLabel id="time-range-label">Time Range</InputLabel>
                    <Select
                        labelId="time-range-label"
                        value={timeRange}
                        label="Time Range"
                        onChange={(e) => setTimeRange(e.target.value as TimeRange)}
                    >
                        <MenuItem value="lastWeek">Last 7 Days</MenuItem>
                        <MenuItem value="lastMonth">Last 30 Days</MenuItem>
                    </Select>
                </FormControl>
                <Typography variant="h6" sx={{ textAlign: "left" }}> {labelMap.currentLabel} </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        justifyContent: "flex-start",
                        mt: 2,
                        borderRadius: 1,
                        backgroundColor: "#fff",
                        boxShadow: 1,
                        width: "1250px",
                    }}
                >
                    {experienceViewStats && (
                        <MetricCard
                            icon={
                                <img src="/images/analytics/launch_icon.png" style={{ width: 40, height: 40 }} />
                            }
                            label="Total Launches"
                            value={experienceViewStats.value}
                            change={experienceViewStats.change}
                            percentage={experienceViewStats.percentage + " vs " + labelMap.previousLabel}
                        />
                    )}

                    <Box sx={{ width: "1px", backgroundColor: "#e0e0e0", my: 2, mx: 1 }} />

                    {viewSessionStats && (
                        <MetricCard
                            icon={
                                <img src="/images/analytics/session_icon.png" style={{ width: 40, height: 40 }} />
                            }
                            label="View Sessions"
                            value={viewSessionStats.value}
                            change={viewSessionStats.change}
                            percentage={viewSessionStats.percentage + " vs " + labelMap.previousLabel}
                        />
                    )}

                    <Box sx={{ width: "1px", backgroundColor: "#e0e0e0", my: 2, mx: 1 }} />

                    {uniqueUsersStats && (
                        <MetricCard
                            icon={
                                <img src="/images/analytics/user_icon.png" style={{ width: 40, height: 40 }} />
                            }
                            label="Unique Users"
                            value={uniqueUsersStats.value}
                            change={uniqueUsersStats.change}
                            percentage={uniqueUsersStats.percentage + " vs " + labelMap.previousLabel}
                        />
                    )}

                    <Box sx={{ width: "1px", backgroundColor: "#e0e0e0", my: 2, mx: 1 }} />

                    {beaconTapsStats && (
                        <MetricCard
                            icon={
                                <img src="/images/analytics/tap_icon.png" style={{ width: 40, height: 40 }} />
                            }
                            label="Total Beacon Taps"
                            value={beaconTapsStats.value}
                            change={beaconTapsStats.change}
                            percentage={beaconTapsStats.percentage + " vs " + labelMap.previousLabel}
                        />
                    )}

                    <Box sx={{ width: "1px", backgroundColor: "#e0e0e0", my: 2, mx: 1 }} />

                    {linkClicksStats && (
                        <MetricCard
                            icon={
                                <img src="/images/analytics/click_icon.png" style={{ width: 40, height: 40 }} />
                            }
                            label="Link Clicks"
                            value={linkClicksStats.value}
                            change={linkClicksStats.change}
                            percentage={linkClicksStats.percentage + " vs " + labelMap.previousLabel}
                        />
                    )}
                </Box>

                <Box sx={{
                    width: "100%",
                    overflowY: "auto",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    p: 2,
                    mb: 2
                }}>
                    {viewCountsChartData.datasets[0] && (
                        <Box>
                            <AreaChart
                                chartData={{
                                    labels: viewCountsChartData.labels,
                                    datasets: [viewCountsChartData.datasets[0]]
                                }}
                                title="Total Launches"
                            />
                        </Box>
                    )}

                    {viewCountsChartData.datasets[1] && (
                        <Box>
                            <AreaChart
                                chartData={{
                                    labels: viewCountsChartData.labels,
                                    datasets: [viewCountsChartData.datasets[1]]
                                }}
                                title="Unique Users"
                            />
                        </Box>
                    )}
                    <Box sx={{ width: "100%" }} />

                    {viewSessionsChartData.datasets[0] && (
                        <Box>
                            <AreaChart
                                chartData={{
                                    labels: viewSessionsChartData.labels,
                                    datasets: [viewSessionsChartData.datasets[0]]
                                }}
                                title="Total Sessions"
                            />
                        </Box>
                    )}

                    {viewSessionsChartData.datasets[1] && (
                        <Box>
                            <AreaChart
                                chartData={{
                                    labels: viewSessionsChartData.labels,
                                    datasets: [viewSessionsChartData.datasets[1]]
                                }}
                                title="Unique Sessions"
                            />
                        </Box>
                    )}

                    <Box sx={{ width: "100%" }} />
                    <Box>
                        <BarChart chartData={beaconInteractionsChartData} title="Beacon Interactions" />
                    </Box>
                    <Box>
                        <DoughnutChart
                            chartData={userAgentChartData}
                            title="Launches by Device"
                            centerText={totalAgentViews}
                        />
                    </Box>
                </Box >
            </>
        );
    }
};

export default Analytics;